export type CertificateStatus =
  | 'valid'
  | 'expired'
  | 'suspended'
  | 'not_found'
  | 'unverifiable'
  | 'not_registry_doc'
  | 'replaced'
  | 'unknown';

export type CertificateIssueReason =
  | 'number_not_recognized'
  | 'number_incomplete'
  | 'registry_not_configured'
  | 'registry_unavailable'
  | 'registry_status_unmapped';

export type CertificateDocType =
  | 'declaration'
  | 'certificate'
  | 'sgr'
  | 'letter'
  | 'unknown';

export interface Certificate {
  id: number;
  productCode: number;
  docType: CertificateDocType;
  number: string;
  numberCanonical: string;
  status: CertificateStatus;
  statusDetail: string | null;
  issueReason: CertificateIssueReason | null;
  actionHint: string | null;
  validFrom: string | null;
  validUntil: string | null;
  officialLink: string | null;
  registryId: string | null;
  verifiedAt: string | null;
  nextCheckAt: string | null;
  isTask: boolean;
  source: string;
}

/**
 * Групповое представление: строка = документ (сертификат), товары — массивом
 * кодов. Так отдают приватные списки CRM; поля `productCode` в группе нет.
 */
export interface CertificateGroup extends Omit<Certificate, 'productCode'> {
  productCount: number;
  products: number[];
}

export type CertificateSort = 'id' | 'verified' | 'relevance';

export interface StaleCertificatesData {
  total: number;
  totalBindings: number;
  limit: number;
  offset: number;
  sort: CertificateSort;
  items: CertificateGroup[];
}

export interface StaleCertificatesResponse {
  data: StaleCertificatesData;
  errors: string[];
}

/** Ответ GET /v1_api_private/certificates/{id} — документ со всеми товарами. */
export interface CertificateResponse {
  data?: CertificateGroup | null;
  errors?: string[];
}

export interface ProductCertificatesData {
  productCode: number;
  total: number;
  certificates?: CertificateGroup[];
}

export interface ProductCertificatesResponse {
  data?: ProductCertificatesData | null;
  errors?: string[];
}

export interface VerifyCertificatesData {
  verified: number;
  failed: number;
  skippedFresh: number;
  certificate: CertificateGroup;
}

export interface VerifyCertificatesResponse {
  data: VerifyCertificatesData;
  errors: string[];
}

export const certificateStatusLabels: Record<string, string> = {
  valid: 'Действует',
  expired: 'Истёк',
  suspended: 'Приостановлен',
  not_found: 'Не найден в реестре',
  unverifiable: 'Невозможно проверить',
  not_registry_doc: 'Не реестровый документ',
  replaced: 'Заменён',
  unknown: 'Неизвестно',
};

export const certificateDocTypeLabels: Record<string, string> = {
  declaration: 'Декларация',
  certificate: 'Сертификат',
  sgr: 'СГР',
  letter: 'Письмо',
  unknown: 'Неизвестно',
};

/** Метки для разбивки `certificatesSummary` карточки товара (ключи camelCase). */
export const certificateSummaryLabels: Record<string, string> = {
  total: 'Всего документов',
  valid: 'Действует',
  expired: 'Истёк',
  suspended: 'Приостановлен',
  notFound: 'Не найден в реестре',
  unverifiable: 'Невозможно проверить',
  notRegistryDoc: 'Не реестровый документ',
  unknown: 'Неизвестно',
  replaced: 'Заменён',
};
